/**
 * Company sign-up — V3 R5, §8.1 `/signup`.
 *
 * Creates a workspace and its owner, then stops: no session is issued, because
 * the workspace is unusable until the email is verified and a half-logged-in
 * state would only confuse. Gated server-side by ENABLE_SIGNUPS; the page shows
 * that plainly rather than failing on submit.
 */

import { useState } from 'react';
import { Link } from 'react-router';
import { Building2, Loader2, MailCheck } from 'lucide-react';
import { AuthLayout } from './AuthLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError, api } from '@/lib/api';

export function SignupPage() {
  const [companyName, setCompanyName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  if (done) {
    return (
      <AuthLayout title="Check your email">
        <div className="flex flex-col items-center gap-3 text-center" data-testid="signup-done">
          <MailCheck className="size-8 text-[var(--accent)]" />
          <p className="text-[13px]">
            We sent a verification link to <strong>{done}</strong>. The workspace becomes usable once
            you confirm the address.
          </p>
          <Link to="/login" className="text-[13px] text-[var(--accent)] hover:underline">
            Back to sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Create your company workspace">
      <form
        className="flex flex-col gap-3"
        data-testid="signup-form"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await api.post('/auth/signup', { companyName, name, email, password });
            setDone(email);
          } catch (err) {
            setError(
              err instanceof ApiError
                ? (err.details?.[0]?.message ?? err.message)
                : 'Could not create the workspace',
            );
          } finally {
            setBusy(false);
          }
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="company">Company name</Label>
          <Input
            id="company"
            autoFocus
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Acme Studios"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="name">Your name</Label>
          <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">Work email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <p className="text-[11px] vt-muted">At least 10 characters.</p>
        </div>

        {error && (
          <p
            role="alert"
            className="rounded-[6px] bg-[var(--danger)]/10 px-2.5 py-2 text-[12px] text-[var(--danger)]"
          >
            {error}
          </p>
        )}

        <Button type="submit" variant="primary" disabled={busy} data-testid="signup-submit">
          {busy ? <Loader2 className="animate-spin" /> : <Building2 />}
          Create workspace
        </Button>

        <p className="text-center text-[12px] vt-muted">
          Already have an account?{' '}
          <Link to="/login" className="text-[var(--accent)] hover:underline">
            Sign in
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
